import Dinero from 'dinero.js';
import { ceil, floor, round } from 'lodash';
import { isUrbanArea } from '~/api/misc2';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import { isEmpty } from './geoUtils';
import {
  type BillingTierCost,
  type Project,
  ProjectGeometryType,
  type Subscription,
  type SubscriptionProjectType,
} from './types';

interface GetCostInputProps {
  projectType: ProjectGeometryType;
  geometry: GeoJSON.MultiLineString | GeoJSON.MultiPolygon;
  subscription: Subscription;
  costMultiplier?: number;
}

interface CostLineItem {
  dimension: string;
  unitCost: string;
  quantity: number;
  total: string;
}

export interface GetCostOutputBasic {
  totalCost: number;
  totalDimension: string;
  pricingExplanation: string;
  totalExplanation: CostLineItem[];
  isUrban: boolean;
}

export interface GetCostOutput {
  draft: GetCostOutputBasic;
  project: GetCostOutputBasic;
  proposal: GetCostOutputBasic;
  projectPlus: GetCostOutputBasic;
}

export function getUrbanOrRuralUnit(isUrban: boolean, costConfig: SubscriptionProjectType): number {
  if (isUrban) {
    return costConfig.urban.unit;
  } else {
    return costConfig.rural.unit;
  }
}

const _ZERO_COST: GetCostOutputBasic = {
  totalCost: 0,
  totalDimension: '',
  pricingExplanation: '',
  totalExplanation: [],
  isUrban: false,
};

export const ZERO_COST: GetCostOutput = {
  draft: _ZERO_COST,
  project: _ZERO_COST,
  proposal: _ZERO_COST,
  projectPlus: _ZERO_COST,
};

const PROJECT_TYPE_MAP: {
  [key in ProjectGeometryType]: keyof BillingTierCost;
} = {
  [ProjectGeometryType.Linear]: 'linear',
  [ProjectGeometryType.Polygon]: 'polygon',
  [ProjectGeometryType.Circle]: 'circle',
  [ProjectGeometryType.Small_3ac]: 'intersection',
};

// NOTE: this method is cloned in api/src/common/billingUtils.ts
export async function getCost({
  projectType,
  geometry,
  subscription,
}: {
  projectType: ProjectGeometryType;
  geometry: GeoJSON.MultiLineString | GeoJSON.MultiPolygon;
  subscription: Subscription;
}): Promise<GetCostOutput> {
  const isUrban: boolean = isEmpty(geometry) ? true : await isUrbanArea(geometry);

  let areaOrLength: { value: number; unit: string };

  if (projectType === ProjectGeometryType.Linear || projectType === ProjectGeometryType.Circle) {
    areaOrLength = calculateLength({
      unitType: 'miles',
      unitSystem: 'metric',
      geometry: geometry as GeoJSON.MultiLineString,
    });
  } else {
    areaOrLength = calculateArea({
      unitType: 'acres',
      unitSystem: 'metric',
      geometry: geometry as GeoJSON.MultiPolygon,
    });
  }

  const getValues = (tier: Project['billingTier']) => {
    let billingTierCostConfig: BillingTierCost;

    if (tier === 'draft') {
      billingTierCostConfig = {
        polygon: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
        linear: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
        circle: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
        intersection: {
          urban: { unit: 1, cost: 0 },
          rural: { unit: 1, cost: 0 },
        },
      };
    } else {
      billingTierCostConfig = subscription.projectCost[tier];
    }

    const projectTypeConfig = billingTierCostConfig[PROJECT_TYPE_MAP[projectType]];
    const perProjectCostConfig = isUrban ? projectTypeConfig.urban : projectTypeConfig.rural;

    const cost = Dinero({ amount: perProjectCostConfig.cost });
    const projectTypeUnit = perProjectCostConfig.unit;

    let numUnits: number;
    let totalExplanation: CostLineItem[];
    let pricingExplanation: string;

    if (areaOrLength.value === 0) {
      numUnits = 0;
      totalExplanation = [
        {
          dimension: `0 ${areaOrLength!.unit}`,
          unitCost: `${cost.toFormat()}/${projectTypeUnit} ${areaOrLength!.unit}`,
          quantity: 0,
          total: `${cost.multiply(0).toFormat()}`,
        },
      ];
      pricingExplanation = `${cost.toFormat()} per ${projectTypeUnit} ${areaOrLength?.unit}`;
    } else if (areaOrLength.value < projectTypeUnit) {
      // less than full unit
      numUnits = 10;
      totalExplanation = [
        {
          dimension: `${areaOrLength!.value || projectTypeUnit} ${areaOrLength!.unit}`,
          unitCost: `${cost.toFormat()}/${projectTypeUnit} ${areaOrLength!.unit}`,
          quantity: 1,
          total: `${cost.divide(10).multiply(numUnits).toFormat()}`,
        },
      ];
      pricingExplanation = `${cost.toFormat()} per ${projectTypeUnit} ${areaOrLength?.unit}`;
    } else {
      numUnits = ceil(Math.max(10, areaOrLength!.value / (projectTypeUnit / 10)));

      if (numUnits % 10 === 0) {
        totalExplanation = [
          {
            dimension: `${round((numUnits / 10) * projectTypeUnit, 2)} ${areaOrLength!.unit}`,
            unitCost: `${cost.toFormat()}/${projectTypeUnit} ${areaOrLength!.unit}`,
            quantity: round(numUnits / 10),
            total: `${cost.divide(10).multiply(numUnits).toFormat()}`,
          },
        ];

        pricingExplanation = `${cost.toFormat()} per ${projectTypeUnit} ${areaOrLength?.unit}`;
      } else {
        const whole = floor(numUnits / 10) * projectTypeUnit;

        const leftOver = round(areaOrLength!.value % projectTypeUnit, 2);

        totalExplanation = [
          {
            dimension: `${whole} ${areaOrLength!.unit}`,
            unitCost: `${cost.toFormat()}/${projectTypeUnit} ${areaOrLength!.unit}`,
            quantity: floor(numUnits / 10),
            total: `${cost.multiply(whole / projectTypeUnit).toFormat()}`,
          },
          {
            dimension: `${leftOver} ${areaOrLength!.unit}`,
            unitCost: `${cost.divide(10).toFormat()}/${round(projectTypeUnit / 10, 2)} ${areaOrLength!.unit}`,
            quantity: ceil(numUnits % 10),
            total: `${cost.multiply((numUnits % 10) / 10).toFormat()}`,
          },
        ];

        pricingExplanation = `${cost.toFormat()} for ${projectTypeUnit} ${
          areaOrLength?.unit
        }, then ${cost.divide(10).toFormat()} per ${round(projectTypeUnit / 10, 2)} ${areaOrLength?.unit}`;
      }
    }

    const totalCost = cost.divide(10).multiply(numUnits).getAmount();

    return {
      totalCost,
      totalDimension: `${areaOrLength!.value} ${areaOrLength.unit}`,
      pricingExplanation,
      totalExplanation,
      isUrban,
    };
  };

  const draft = getValues('draft');
  const proposal = getValues('proposal');
  const project = getValues('project');
  const projectPlus = getValues('projectPlus');

  return { draft, proposal, project, projectPlus };
}

export function isZeroCostSubscription(subscription: Subscription): boolean {
  const billingTiers = Object.keys(subscription.projectCost);

  const allZero = billingTiers.every((tier) => {
    const tierConfig = subscription.projectCost[tier as keyof Subscription['projectCost']];

    return Object.values(tierConfig).every((costConfig: SubscriptionProjectType) => {
      return costConfig.rural.cost === 0 && costConfig.urban.cost === 0;
    });
  });

  return allZero;
}
